import { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import ProfileDropdown from '../components/ProfileDropdown'
import { 
  CameraIcon,
  CheckIcon,
  CogIcon,
  SparklesIcon,
  RocketLaunchIcon,
  BeakerIcon,
  LightBulbIcon,
  ArrowRightIcon,
  CalendarIcon,
  UserGroupIcon,
  CloudIcon,
  DevicePhoneMobileIcon,
  ChartBarIcon,
  Bars3Icon,
  XMarkIcon,
  ClockIcon
} from '@heroicons/react/24/outline'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWandSparkles } from '@fortawesome/free-solid-svg-icons'
import CurrencySelector from '../components/CurrencySelector'

const RoadmapPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Check if user is logged in
  const isLoggedIn = !!user

  // Enhanced workflow features with simple descriptions
  const enhancedWorkflowFeatures = [
    { name: "Advanced Calendar Integration", status: "developing", icon: CalendarIcon, description: "Seamless integration with major calendar platforms including conflict detection and weather forecasts" },
    { name: "Client Portal Enhancements", status: "developing", icon: UserGroupIcon, description: "Advanced client collaboration with real-time updates, mood boards, and custom branding" },
    { name: "Automated Email Sequences", status: "in-focus", icon: RocketLaunchIcon, description: "Smart email automation for onboarding, updates, and follow-ups with A/B testing" },
    { name: "Contract Management & E-Signatures", status: "in-focus", icon: CheckIcon, description: "Complete contract lifecycle with e-signatures, version control, and compliance tracking" },
    { name: "Advanced Analytics Dashboard", status: "in-focus", icon: ChartBarIcon, description: "Business intelligence with revenue forecasting and profitability tracking" },
    { name: "Team Collaboration Tools", status: "in-focus", icon: UserGroupIcon, description: "Multi-user workspace with role permissions, chat, and collaborative planning" },
    { name: "Mobile App Beta (iOS/Android)", status: "developing", icon: DevicePhoneMobileIcon, description: "Native mobile apps for on-the-go management with offline capabilities" },
    { name: "Advanced File Upload & Storage", status: "developing", icon: CloudIcon, description: "Enterprise file management with unlimited storage and cloud integrations" },
    { name: "Advanced File Management", status: "in-focus", icon: CloudIcon, description: "AI-powered file organization with tagging, duplicate detection, and automation" },
    { name: "Time Tracking & Billing", status: "in-focus", icon: ClockIcon, description: "Comprehensive time tracking with automatic billing and accounting integrations" },
    { name: "Project Templates", status: "in-focus", icon: LightBulbIcon, description: "Pre-built templates for different genres with automated task lists and timelines" },
    { name: "Client Feedback & Approval System", status: "in-focus", icon: UserGroupIcon, description: "Streamlined review process with visual annotations and approval workflows" }
  ]

  const roadmapPhases = [
    {
      phase: "Current (Q1-Q2 2025)",
      status: "live",
      title: "Foundation & Core Features",
      description: "Building the essential tools for freelance photographers and videographers",
      progress: 100,
      features: [
        { name: "User Authentication & Profiles", status: "completed", icon: UserGroupIcon, description: "Secure user registration, login, and profile management" },
        { name: "Client Management System", status: "completed", icon: UserGroupIcon, description: "Comprehensive client database with contact information and project history" },
        { name: "Basic Shoot Scheduling", status: "completed", icon: CalendarIcon, description: "Calendar-based shoot scheduling with basic conflict detection" },
        { name: "Invoice Generation & Management", status: "completed", icon: ChartBarIcon, description: "Professional invoice creation, tracking, and payment status management" },
        { name: "Task Management System", status: "completed", icon: CheckIcon, description: "Project-based task creation, assignment, and completion tracking" },
        { name: "Multi-Currency Support", status: "completed", icon: SparklesIcon, description: "Support for multiple currencies with real-time conversion rates" },
        { name: "Payment Processing Integration", status: "completed", icon: CogIcon, description: "Secure payment processing through Stripe with multiple payment methods" },
        { name: "Responsive Dashboard", status: "completed", icon: ChartBarIcon, description: "Mobile-friendly dashboard with key metrics and quick actions" },
        { name: "Email Notifications", status: "completed", icon: RocketLaunchIcon, description: "Automated email alerts for important events and deadlines" },
        { name: "Basic AI Task Suggestions", status: "completed", icon: SparklesIcon, description: "AI-powered recommendations for task prioritization and workflow optimization" },
        { name: "User Settings & Preferences", status: "completed", icon: CogIcon, description: "Customizable user preferences and application settings" },
        { name: "Basic Reporting & Analytics", status: "completed", icon: ChartBarIcon, description: "Essential business reports and performance analytics" }
      ]
    },
    {
      phase: "Next (Q3 2025)",
      status: "in-development",
      title: "Enhanced Workflow & Automation",
      description: "Streamlining workflows with AI-powered automation and advanced features",
      progress: 20,
      features: enhancedWorkflowFeatures.map(f => ({
        ...f,
        status: ['Advanced Calendar Integration', 'Automated Email Sequences', 'Advanced Analytics Dashboard', 'Team Collaboration Tools'].includes(f.name)
          ? 'in-progress'
          : f.status
      }))
    },
    {
      phase: "Future (Q4 2025 - Q1 2026)",
      status: "research",
      title: "Advanced Features & Scaling",
      description: "Enterprise-grade features and innovative tools for growing creative businesses",
      progress: 0,
      features: [
        { name: "AI-Powered Task Automation", status: "research", icon: SparklesIcon, description: "Fully automated task creation and management using advanced AI algorithms" },
        { name: "AI Photo/Video Analysis & Tagging", status: "research", icon: BeakerIcon, description: "Intelligent content analysis with automatic tagging and metadata extraction" },
        { name: "Advanced Project Templates", status: "research", icon: LightBulbIcon, description: "Industry-specific templates with smart automation and customization options" },
        { name: "White-Label Solutions", status: "research", icon: SparklesIcon, description: "Fully customizable platform branding for agencies and larger studios" },
        { name: "API & Third-Party Integrations", status: "research", icon: CogIcon, description: "Comprehensive API access and integrations with popular creative tools" },
        { name: "Advanced Reporting & Business Intelligence", status: "research", icon: ChartBarIcon, description: "Deep analytics with predictive insights and custom dashboard creation" },
        { name: "Multi-Language Support", status: "research", icon: UserGroupIcon, description: "Full internationalization with support for multiple languages and regions" },
        { name: "Enterprise Security & Compliance", status: "research", icon: CheckIcon, description: "Advanced security features meeting enterprise and regulatory requirements" },
        { name: "Custom Workflow Builder", status: "research", icon: CogIcon, description: "Visual workflow designer for creating custom business processes and automation" },
        { name: "AI Client Communication Assistant", status: "research", icon: RocketLaunchIcon, description: "AI-powered assistant for automated client communication and support responses" },
        { name: "Automated Backup & Disaster Recovery", status: "research", icon: CloudIcon, description: "Enterprise-grade backup solutions with automatic disaster recovery protocols" },
        { name: "Advanced Portfolio Management", status: "research", icon: CameraIcon, description: "Professional portfolio creation with advanced customization and SEO optimization" },
        { name: "Social Media Integration & Auto-Posting", status: "research", icon: SparklesIcon, description: "Automated social media management with intelligent content scheduling" },
        { name: "Equipment & Inventory Management", status: "research", icon: CogIcon, description: "Complete equipment tracking with maintenance schedules and availability monitoring" },
        { name: "Advanced Pricing & Quote Generator", status: "research", icon: ChartBarIcon, description: "AI-powered pricing optimization with dynamic quote generation and market analysis" },
        { name: "Client Relationship Management (CRM)", status: "research", icon: UserGroupIcon, description: "Advanced CRM features with lead tracking and conversion analytics" },
        { name: "Marketplace for Creative Services", status: "research", icon: LightBulbIcon, description: "Platform marketplace connecting creative professionals with potential clients" },
        { name: "Virtual Reality Shoot Planning", status: "research", icon: BeakerIcon, description: "VR-powered location scouting and shoot planning with immersive previews" }
      ]
    }
  ]

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-emerald-400 bg-emerald-500/20'
      case 'in-progress': return 'text-blue-400 bg-blue-500/20'
      case 'developing': return 'text-blue-400 bg-blue-500/20'
      case 'in-focus': return 'text-purple-400 bg-purple-500/20'
      case 'planned': return 'text-purple-400 bg-purple-500/20'
      case 'research': return 'text-orange-400 bg-orange-500/20'
      default: return 'text-gray-400 bg-gray-500/20'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Shot & Delivered'
      case 'in-progress': return 'In Progress'
      case 'developing': return 'In Development'
      case 'in-focus': return 'In Focus'
      case 'planned': return 'Planned'
      case 'research': return 'Scouting'
      default: return 'Unknown'
    }
  }

  const getPhaseColor = (status: string) => {
    switch (status) {
      case 'live': return 'from-emerald-500 to-teal-500'
      case 'in-development': return 'from-blue-500 to-purple-500'
      case 'planned': return 'from-purple-500 to-pink-500'
      case 'research': return 'from-orange-500 to-red-500'
      default: return 'from-gray-500 to-gray-600'
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <header className="relative z-20 glass-header">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 lg:py-6">
            <div className="flex items-center space-x-3">
              <motion.button 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 lg:space-x-3 hover:opacity-80 transition-opacity"
              >
                <div className="w-10 h-10 lg:w-12 lg:h-12 glass-badge rounded-2xl flex items-center justify-center">
                  <CameraIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                </div>
                <span className="text-xl lg:text-2xl font-bold text-white">
                  Shoot<span className="text-gray-400">Suite</span>
                </span>
              </motion.button>
              
              {/* Beta Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="relative"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full blur-sm opacity-60 animate-pulse"></div>
                  <div className="relative glass-badge rounded-full px-3 py-1 border-2 border-transparent bg-gradient-to-r from-emerald-500/20 to-teal-500/20 backdrop-blur-sm">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 opacity-50"></div>
                    <div className="absolute inset-[2px] rounded-full bg-black/80 backdrop-blur-sm"></div>
                    <div className="relative text-xs font-semibold text-white">
                      Now In Beta
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              <motion.button
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                onClick={() => navigate('/')}
                className="text-white/80 hover:text-white transition-colors font-medium"
              >
                Home
              </motion.button>
              <motion.button
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-white font-medium"
              >
                Roadmap
              </motion.button>
              <motion.button
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                onClick={() => navigate('/updates')}
                className="text-white/80 hover:text-white transition-colors font-medium"
              >
                Updates
              </motion.button>
              
              {isLoggedIn ? (
                <ProfileDropdown />
              ) : (
                <div className="flex items-center space-x-4">
                  <motion.button
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    onClick={() => navigate('/auth')}
                    className="text-white/80 hover:text-white transition-colors font-medium"
                  >
                    Sign In
                  </motion.button>
                  <motion.button
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    onClick={() => navigate('/auth')}
                    className="gradient-button"
                  >
                    Get Started
                  </motion.button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-white p-2"
              >
                {isMobileMenuOpen ? (
                  <XMarkIcon className="w-6 h-6" />
                ) : (
                  <Bars3Icon className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden border-t border-white/10 py-4"
            >
              <div className="flex flex-col space-y-4">
                <button
                  onClick={() => {
                    navigate('/')
                    setIsMobileMenuOpen(false)
                  }}
                  className="text-white/80 hover:text-white transition-colors font-medium text-left"
                >
                  Home
                </button>
                <button className="text-white font-medium text-left">
                  Roadmap
                </button>
                <button
                  onClick={() => {
                    navigate('/updates')
                    setIsMobileMenuOpen(false)
                  }}
                  className="text-white/80 hover:text-white transition-colors font-medium text-left"
                >
                  Updates
                </button>
                
                {!isLoggedIn && (
                  <>
                    <button
                      onClick={() => {
                        navigate('/auth')
                        setIsMobileMenuOpen(false)
                      }}
                      className="text-white/80 hover:text-white transition-colors font-medium text-left"
                    >
                      Sign In
                    </button>
                    <button
                      onClick={() => {
                        navigate('/auth')
                        setIsMobileMenuOpen(false)
                      }}
                      className="gradient-button text-center"
                    >
                      Get Started
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main className="relative z-10">
        {/* Hero Section */}
        <section className="pt-20 pb-16 lg:pt-32 lg:pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="mb-8"
              >
                <div className="inline-flex items-center space-x-2 glass-badge rounded-full px-4 py-2 mb-6">
                  <RocketLaunchIcon className="w-5 h-5 text-blue-400" />
                  <span className="text-white font-medium">Development Roadmap</span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                  Building the Future of
                  <span className="block bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                    Creative Workflow
                  </span>
                </h1>
                
                <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                  Discover what we've built, where we are now in Q2 2025, and what's coming next. 
                  Our roadmap is driven by your feedback and the evolving needs of creative professionals.
                </p>
              </motion.div>

              {/* Progress Bar with Phase Indicators */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="max-w-4xl mx-auto mt-12"
              >
                <div className="relative">
                  {/* Progress Bar Background */}
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: "58%" }}
                      transition={{ duration: 2, delay: 0.6, ease: "easeOut" }}
                      className="h-full bg-gradient-to-r from-emerald-500 via-blue-500 to-purple-500 rounded-full relative"
                    >
                      {/* Animated glow effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-blue-500 to-purple-500 rounded-full blur-sm opacity-60 animate-pulse"></div>
                    </motion.div>
                  </div>

                  {/* Phase Indicators */}
                  <div className="absolute -top-3 left-0 w-full flex justify-between items-center">
                    {/* Phase 1 - Current (Completed) */}
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.5, delay: 1.0 }}
                      className="relative flex flex-col items-center"
                    >
                      <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full border-4 border-black flex items-center justify-center shadow-lg">
                        <CheckIcon className="w-4 h-4 text-white" />
                      </div>
                      <div className="absolute top-10 text-center">
                        <div className="text-xs font-semibold text-emerald-400 whitespace-nowrap">Q1-Q2 2025</div>
                        <div className="text-xs text-gray-400 whitespace-nowrap">Foundation</div>
                      </div>
                    </motion.div>

                    {/* Phase 2 - Next (In Development) */}
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.5, delay: 1.2 }}
                      className="relative flex flex-col items-center"
                    >
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full border-4 border-black flex items-center justify-center shadow-lg">
                        <CogIcon className="w-4 h-4 text-white animate-spin" />
                      </div>
                      <div className="absolute top-10 text-center">
                        <div className="text-xs font-semibold text-blue-400 whitespace-nowrap">Q3 2025</div>
                        <div className="text-xs text-gray-400 whitespace-nowrap">In Development</div>
                      </div>
                    </motion.div>

                    {/* Phase 3 - Future (Planned) */}
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.5, delay: 1.4 }}
                      className="relative flex flex-col items-center"
                    >
                      <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full border-4 border-black flex items-center justify-center shadow-lg opacity-60">
                        <BeakerIcon className="w-4 h-4 text-white" />
                      </div>
                      <div className="absolute top-10 text-center">
                        <div className="text-xs font-semibold text-purple-400 whitespace-nowrap">Q4 2025+</div>
                        <div className="text-xs text-gray-400 whitespace-nowrap">Advanced</div>
                      </div>
                    </motion.div>
                  </div>

                  {/* Progress Percentage */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 1.6 }}
                    className="text-center mt-16"
                  >
                    <div className="text-3xl font-bold text-white mb-2">58% Complete</div>
                    <div className="text-gray-400">Overall Development Progress</div>
                  </motion.div>
                </div>
              </motion.div>

              {/* Scroll Indicator Widget */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.8 }}
                className="flex flex-col items-center mt-16"
              >
                <div className="text-gray-400 text-sm font-medium mb-4">
                  Explore our development journey
                </div>
                <motion.div
                  animate={{ y: [0, 8, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center"
                >
                  <motion.div
                    animate={{ y: [0, 12, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    className="w-1 h-3 bg-gradient-to-b from-blue-400 to-purple-500 rounded-full mt-2"
                  ></motion.div>
                </motion.div>
                <div className="text-gray-500 text-xs mt-2">Scroll down</div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Current Status Overview */}
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Where We Are Today
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                ShootSuite is currently in beta with all core features complete and actively being used by creative professionals worldwide.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="glass-mockup rounded-2xl p-8 text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CheckIcon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">100%</h3>
                <p className="text-gray-300">Core Features Complete</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="glass-mockup rounded-2xl p-8 text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <UserGroupIcon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">2.5K+</h3>
                <p className="text-gray-300">Beta Users</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="glass-mockup rounded-2xl p-8 text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <SparklesIcon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">24/7</h3>
                <p className="text-gray-300">Active Development</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Roadmap Phases */}
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Development Phases
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Our development is organized into focused phases, each building upon the last to create a comprehensive creative workflow platform.
              </p>
            </motion.div>

            <div className="space-y-12">
              {roadmapPhases.map((phase, phaseIndex) => (
                <motion.div
                  key={phaseIndex}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 + phaseIndex * 0.1 }}
                  className="glass-mockup rounded-3xl p-8 lg:p-12"
                >
                  {/* Phase Header */}
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
                    <div className="mb-4 lg:mb-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className={`px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r ${getPhaseColor(phase.status)} text-white`}>
                          {phase.phase}
                        </div>
                        {phase.status === 'live' && (
                          <div className="flex items-center space-x-1">
                            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                            <span className="text-emerald-400 text-sm font-medium">Live</span>
                          </div>
                        )}
                      </div>
                      <h3 className="text-2xl lg:text-3xl font-bold text-white mb-2">
                        {phase.title}
                      </h3>
                      <p className="text-gray-300 text-lg max-w-2xl">
                        {phase.description}
                      </p>
                    </div>
                    
                    {(phase.status === 'live' || phase.status === 'in-development') && (
                      <div className="text-right">
                        <div className="text-3xl font-bold text-white mb-1">
                          {phase.progress}%
                        </div>
                        <div className="text-gray-400 text-sm">
                          {phase.status === 'live' ? 'Complete' : 'Started'}
                        </div>
                        <div className="w-32 h-2 bg-white/10 rounded-full mt-2">
                          <div 
                            className={`h-full rounded-full transition-all duration-1000 ${
                              phase.status === 'live' 
                                ? 'bg-gradient-to-r from-emerald-500 to-teal-500' 
                                : 'bg-gradient-to-r from-blue-500 to-purple-500'
                            }`}
                            style={{ width: `${phase.progress}%` }}
                          ></div>
                        </div>
                        {phase.status === 'in-development' && (
                          <div className="text-xs text-gray-500 mt-1">
                            Early development phase
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                                    {/* Features Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {phase.features.map((feature, featureIndex) => (
                      <motion.div
                        key={featureIndex}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.5 + featureIndex * 0.05 }}
                        className="p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-200"
                      >
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="flex-shrink-0">
                            <feature.icon className="w-5 h-5 text-gray-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-white font-medium text-sm">{feature.name}</span>
                          </div>
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(feature.status)} flex-shrink-0`}>
                            {getStatusText(feature.status)}
                          </div>
                        </div>
                        {'description' in feature && (
                          <p className="text-gray-400 text-xs leading-relaxed">
                            {feature.description}
                          </p>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Community Feedback Section */}
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="glass-mockup rounded-3xl p-8 lg:p-12 text-center"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <LightBulbIcon className="w-8 h-8 text-white" />
              </div>
              
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Shape Our Roadmap
              </h2>
              
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                Your feedback drives our development. Join our community to suggest features, 
                vote on priorities, and help us build the perfect creative workflow platform.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="gradient-button flex items-center space-x-2"
                >
                  <span>Join Beta Program</span>
                  <ArrowRightIcon className="w-5 h-5" />
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="glass-button flex items-center space-x-2"
                >
                  <span>Submit Feedback</span>
                  <FontAwesomeIcon icon={faWandSparkles} className="w-4 h-4" />
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="glass-footer py-12 lg:py-16 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-2 lg:space-x-3">
              <div className="w-10 h-10 lg:w-12 lg:h-12 glass-badge rounded-2xl flex items-center justify-center">
                <CameraIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
              </div>
              <span className="text-xl lg:text-2xl font-bold text-white">
                Shoot<span className="text-gray-400">Suite</span>
              </span>
            </div>
            
            {/* Currency Selector and Copyright */}
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
              <div className="text-gray-400 text-sm font-medium text-center md:text-left">
                © 2025 ShootSuite. Built with ❤️ by <a href="https://www.instagram.com/evanball.jpg/" className="text-white hover:text-gray-300">evanball.jpg</a> for creative professionals.
              </div>
              <div className="flex items-center space-x-3">
                <motion.a
                  href="http://instagram.com/shootsuiteapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg 
                    className="w-4 h-4" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.40s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  <span>@shootsuiteapp</span>
                </motion.a>
                <CurrencySelector />
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default RoadmapPage 